import type { CategoryTreeNode } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import type { StoreProductsParams } from '@/entities/product';
import { Button } from '@/shared/ui';
import { StoreCategoryChip } from './store-category-chip';
import { StoreCategoryTab } from './store-category-tab';

interface StoreCategoryTabsProps {
  readonly tree: readonly CategoryTreeNode[];
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
  readonly filters: StoreProductsParams;
  /** 카테고리만 바꾼 조건을 올린다 — 1페이지 되돌림은 페이지 컴포넌트의 몫이다. */
  readonly onFiltersChange: (filters: StoreProductsParams) => void;
}

/** 선택한 카테고리가 속한 대분류 — 대분류 자신이거나 그 아래 소분류면 그 대분류다. 모르는 id 면 없음. */
const findSelectedParent = (
  tree: readonly CategoryTreeNode[],
  categoryId: string | undefined,
): CategoryTreeNode | undefined =>
  categoryId === undefined
    ? undefined
    : tree.find(
        (parent) =>
          parent.id === categoryId || parent.children.some((child) => child.id === categoryId),
      );

/**
 * 목록 위 카테고리 탭 — 전폭 얇은 선 위에 "전체"와 대분류 탭(밑줄 선택)을 한 줄로 두고,
 * 좁은 화면에서는 가로로 밀어 본다. 대분류(또는 그 소분류)를 고르면 아래 줄에
 * "전체 {대분류}"와 소분류 알약이 열린다. 대분류 id 는 서버가 소분류까지 넓혀 거른다.
 */
export const StoreCategoryTabs = ({
  tree,
  isPending,
  isError,
  onRetry,
  filters,
  onFiltersChange,
}: StoreCategoryTabsProps): ReactElement => {
  const selectedId = filters.categoryId;
  const selectedParent = findSelectedParent(tree, selectedId);
  const select = (categoryId: string | undefined): void =>
    onFiltersChange({ ...filters, categoryId });

  return (
    <nav aria-label="카테고리">
      {isPending ? (
        // 불러오는 동안은 글자 없는 회색 막대로 탭 줄 자리를 지킨다.
        <>
          <p role="status" className="sr-only">
            카테고리를 불러오는 중입니다.
          </p>
          <div aria-hidden className="flex h-12 items-center gap-8 border-b border-foreground/10">
            <div className="h-4 w-10 bg-muted" />
            <div className="h-4 w-16 bg-muted" />
            <div className="h-4 w-14 bg-muted" />
            <div className="h-4 w-12 bg-muted" />
          </div>
        </>
      ) : isError ? (
        // 로드 실패를 "카테고리 없음"처럼 보이게 두지 않는다 — 명시 안내 + 재시도.
        <div className="flex flex-wrap items-center gap-3 border-b border-foreground/10 pb-4">
          <p role="alert" className="text-sm text-destructive">
            카테고리를 불러오지 못했습니다.
          </p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            다시 시도
          </Button>
        </div>
      ) : (
        <>
          {/* 탭이 넘치면 가로로 민다 — 얇은 선은 스크롤 폭 전체에 걸치도록 목록 자신에 긋는다. */}
          <div className="-mx-5 overflow-x-auto px-5 sm:mx-0 sm:px-0">
            <ul className="flex w-max min-w-full gap-7 border-b border-foreground/10 sm:gap-10">
              <StoreCategoryTab
                label="전체"
                isSelected={selectedId === undefined}
                onSelect={() => select(undefined)}
              />
              {tree.map((parent) => (
                <StoreCategoryTab
                  key={parent.id}
                  label={parent.name}
                  isSelected={selectedParent?.id === parent.id}
                  onSelect={() => select(parent.id)}
                />
              ))}
            </ul>
          </div>

          {/* 소분류가 없는 대분류는 알약 줄을 열지 않는다 — "전체 {대분류}" 하나만 남는 줄은 무의미하다. */}
          {selectedParent !== undefined && selectedParent.children.length > 0 ? (
            <ul
              aria-label={`${selectedParent.name} 세부 카테고리`}
              className="mt-5 flex flex-wrap gap-2"
            >
              <StoreCategoryChip
                label={`전체 ${selectedParent.name}`}
                isSelected={selectedId === selectedParent.id}
                onSelect={() => select(selectedParent.id)}
              />
              {selectedParent.children.map((child) => (
                <StoreCategoryChip
                  key={child.id}
                  label={child.name}
                  isSelected={selectedId === child.id}
                  onSelect={() => select(child.id)}
                />
              ))}
            </ul>
          ) : null}
        </>
      )}
    </nav>
  );
};
